<template>
  <div v-if="subscription">
    <app-title-bar>
      <app-material-icon name="person" />
      <h2 class="text-text-primary">{{ $t('subscriptions.views.SubscriptionSubscribers.title') }}</h2>
    </app-title-bar>

    <div class="d-flex flex-column row-gap-6 layout-spacing">
      <AppCard elevation="0" class="SubscriptionSubscribers-card">
        <template v-if=" subscriptionPending">
          <v-skeleton-loader type="text" class="mb-1"/>
          <v-skeleton-loader type="text"/>
        </template>
        <div
          v-else
          class="text-text-secondary"
        >
          <i18n-t keypath="subscriptions.views.SubscriptionSubscribers.startDate"
                  tag="div"
                  scope="global"
                  class="mb-2"
          >
            <template #date>
              <h3 class="d-inline">{{ ISOToDDMMYYYY(subscription.startDate) }}</h3>
            </template>
          </i18n-t>
          <i18n-t keypath="subscriptions.views.SubscriptionSubscribers.renewalDate"
                  tag="div"
                  scope="global"
                  class="mb-2"
          >
            <template #date>
              <h3 class="d-inline">{{ ISOToDDMMYYYY(subscription.commitmentDate) }}</h3>
            </template>
          </i18n-t>
          <i18n-t keypath="subscriptions.views.SubscriptionSubscribers.seats"
                  tag="div"
                  scope="global"
          >
            <template #count>
              <h3 class="d-inline">
                {{ subscription.billingSeatsAvailable === 999 ? $t('common.unlimited') : subscription.billingSeats }}
              </h3>
            </template>
          </i18n-t>
        </div>
      </AppCard>

      <div class="d-flex align-center column-gap-2">
        <SearchTextField v-model="searchQuery" />
        <AddSubscriberDialog class="d-inline-block ml-auto"
                             :subscription="subscription"
                             @postNewSubscriberSuccess="onPostNewSubscriberSuccess"
        />

        <AppDownloadFile :link="exportLink">
          <template #default="{submitForm}">
            <app-button
              type="outlined"
              variant="neutral"
              is-icon-color-custom
              @click="submitForm"
            >
              <template #left-icon>
                <app-material-icon name="file_export" />
              </template>
              {{ $t('subscriptions.views.SubscriptionSubscribers.export') }}
            </app-button>
          </template>
        </AppDownloadFile>
      </div>

      <template v-if="subscriptionUsers && !subscriptionUsersPending">
        <v-data-table :headers="headers"
                      :items-per-page="itemsPerPage"
                      :items="filteredSubscriptionUsers"
                      :page="currentPage"
                      class="SubscriptionSubscribers-table"
                      show-expand
                      v-model:expanded="expanded"
        >
          <template #item.data-table-expand="{ item }">
            <app-button
              type="icon"
              @click="onExpandClick(item)"
            >
              <app-material-icon
                name="keyboard_arrow_right"
                class="SubscriptionSubscribers-expand"
                :class="{'SubscriptionSubscribers-expand--open': expanded.includes(item.id)}"
              />
            </app-button>
          </template>

          <template #expanded-row="{ columns, item }">
            <tr>
              <td v-if="!mobile" />
              <td :colspan="columns.length - 1">
                <div class="d-flex column-gap-4">
                  <div>
                    <div class="collapsed-info-title">
                      {{ $t('subscriptions.views.SubscriptionSubscribers.lastLogin') }}
                    </div>
                    <div>
                      {{ ISOToShortenedDate(item.lastLogin) }}
                    </div>
                  </div>

                  <div>
                    <div class="collapsed-info-title">
                      {{ $t('subscriptions.views.SubscriptionSubscribers.activeRooms') }}
                    </div>
                    <div>{{ item.activeRooms }}</div>
                  </div>
                </div>
              </td>
            </tr>
          </template>

          <template #item.firstName="{item}">
            <AppTooltip>
              <template #activator="{ props }">
                <div class="SubscriptionSubscribers-table-firstName text-truncated" v-bind="props">
                  {{ item.firstName }}
                </div>
              </template>
              <span>{{ item.firstName }}</span>
            </AppTooltip>
          </template>

          <template #item.lastName="{item}">
            <AppTooltip>
              <template #activator="{ props }">
                <div class="SubscriptionSubscribers-table-lastName text-truncated" v-bind="props">
                  {{ item.lastName }}
                </div>
              </template>
              <span>{{ item.lastName }}</span>
            </AppTooltip>
          </template>

          <template #item.email="{item}">
            <AppTooltip>
              <template #activator="{ props }">
                <div class="SubscriptionSubscribers-table-email text-truncated" v-bind="props">
                  {{ item.email }}
                </div>
              </template>
              <span>{{ item.email }}</span>
            </AppTooltip>
          </template>

          <template #item.participant="{item}">
            <app-toggle
              :disabled="item.isSubUser && profile.id === item.id"
              :loading="patchSubAdminUserPendingIds.includes(item.id)"
              :model-value="item.isSubUser"
              class="d-flex justify-center td-mobile-view"
              density="compact"
              hide-details
              @update:model-value="onSubAdminUserChange(item)"
            />
          </template>

          <template #item.administrator="{item}">
            <app-toggle
              :disabled="item.isSubUser && profile.id === item.id"
              :loading="patchSubAdminPendingIds.includes(item.id)"
              :model-value="item.isSubAdmin"
              class="d-flex justify-center td-mobile-view"
              density="compact"
              hide-details
              @update:model-value="onSubAdminChange(item)"
            />
          </template>

          <template #item.accounting="{item}">
            <app-toggle
              :loading="patchSubAdminBillingPendingIds.includes(item.id)"
              :model-value="item.isSubBillingAdmin"
              class="d-flex justify-center td-mobile-view"
              density="compact"
              hide-details
              @update:model-value="onSubAdminBillingChange(item)"
            />
          </template>

          <template #item.archivingResponsible="{item}">
            <div class="d-flex justify-center td-mobile-view">
              <AppTooltip>
                <template #activator="{ props }">
                  <div v-bind="props">
                    <app-toggle
                      :model-value="item.isSubArchivingAdmin"
                      density="compact"
                      disabled
                      hide-details
                    />
                  </div>
                </template>
                <span>{{ $t('subscriptions.views.SubscriptionSubscribers.archivingResponsibleTooltip') }}</span>
              </AppTooltip>
            </div>
          </template>

          <template #item.options="{item}">
            <app-button
              type="icon"
              @click="openDeleteUserDialog(item)"
            >
              <app-material-icon name="delete" />
            </app-button>
          </template>

          <template #bottom>
            <div class="d-flex justify-end mt-4">
              <AppPagination v-model:current-page="currentPage"
                              v-model:page-size="itemsPerPage"
                              :pageSizes="ITEMS_PER_PAGE"
                              :total="filteredSubscriptionUsers.length"
              />
            </div>
          </template>
        </v-data-table>
      </template>
      <template v-else-if="subscriptionUsersPending">
        <AppDataTableSkeletonLoader class="mt-4" />
      </template>
    </div>

    <SubscriptionSubscribersDeleteDialog :is-open="deleteUserDialogIsOpen"
                                         :user-to-delete="userToDelete"
                                         @close="closeDeleteUserDialog"
                                         @userDeleteSuccess="userDeleteSuccess"
    />

    <app-modal :model-value="!!subAddConfirmationDialogInfo"
               size="xs"
               :ok-disabled="seatsPending || seatsError"
               :cancel-disabled="seatsPending"
               @ok="confirmSubAddConfirmationDialog"
               @cancel="cancelSubAddConfirmationDialog"
    >
      <template #title>{{ $t('subscriptions.views.SubscriptionSubscribers.confirmationDialogTitle') }}</template>
      <template #body>
        <div v-if="seatsPending" class="text-center">
          <v-progress-circular indeterminate size="48" />
        </div>
        <p v-else>{{subAddConfirmationDialogText}}</p>
      </template>
    </app-modal>

    <app-modal :model-value="!!subRemoveConfirmationDialogInfo"
               size="xs"
               :ok-text="$t('common.ok')"
               @ok="confirmSubRemoveConfirmationDialog"
               @cancel="cancelSubRemoveConfirmationDialog"
      >
      <template #title>{{ $t('subscriptions.views.SubscriptionSubscribers.confirmationDialogTitle') }}</template>
      <template #body>
        <p>
          {{ $t('subscriptions.views.SubscriptionSubscribers.confirmationDialogSubRemoveText', { subName: subscription.name }) }}
        </p>
      </template>
    </app-modal>
  </div>
</template>

<script>
import { useDisplay } from 'vuetify'
import { mapActions, mapGetters, mapState, useStore } from 'vuex'

import AppCard from '@/common/AppCard.vue'
import AppDataTableSkeletonLoader from '@/common/AppDataTableSkeletonLoader.vue'
import AppDownloadFile from '@/common/AppDownloadFile.vue'
import AppTooltip from '@/common/AppTooltip.vue'
import AppPagination from '@/common/pagination/AppPagination.vue'
import { usePagination } from '@/common/pagination/usePagination'
import SearchTextField from '@/common/SearchTextField.vue'
import { ISOToDDMMYYYY, ISOToShortenedDate } from '@/common/utils/dates'
import { toast } from '@/common/utils/toast'
import {
  GET_SUBSCRIPTION_USERS,
  PATCH_SUB_ADMIN,
  PATCH_SUB_ADMIN_BILLING, PATCH_SUB_ADMIN_USER,
  GET_SUBSCRIPTION,
  GET_SEATS,
} from '@/store/modules/subscription/action_types'
import AddSubscriberDialog from '@/subscriptions/AddSubscriberDialog.vue'
import SubscriptionSubscribersDeleteDialog from '@/subscriptions/SubscriptionSubscribersDeleteDialog.vue'

import { SUBSCRIPTION_SUBSCRIBERS_ITEMS_PER_PAGE } from '../constants.js'

const ITEMS_PER_PAGE = [50, 100, 250]

export default {
  name: 'SubscriptionSubscribers',
  components: {
    AppCard,
    AddSubscriberDialog,
    AppDataTableSkeletonLoader,
    AppDownloadFile,
    AppPagination,
    AppTooltip,
    SearchTextField,
    SubscriptionSubscribersDeleteDialog,
  },
  setup: () => {
    const { mobile } = useDisplay()
    const store = useStore()
    const { currentPage, itemsPerPage } = usePagination({
      currentUserId: store.getters['user/currentUserId'],
      userSettingsKey: SUBSCRIPTION_SUBSCRIBERS_ITEMS_PER_PAGE,
      pageSize: ITEMS_PER_PAGE[0],
    })

    return {
      mobile,
      currentPage,
      itemsPerPage,
    }
  },
  data () {
    return {
      ISOToDDMMYYYY,
      ISOToShortenedDate,
      headers: [
        { title: '', key: 'data-table-expand' },
        { value: 'firstName', title: this.$t('subscriptions.views.SubscriptionSubscribers.headers.firstName'), sortable: true },
        { value: 'lastName', title: this.$t('subscriptions.views.SubscriptionSubscribers.headers.lastName'), sortable: true },
        { value: 'email', title: this.$t('subscriptions.views.SubscriptionSubscribers.headers.email'), sortable: true },
        { value: 'participant', title: this.$t('subscriptions.views.SubscriptionSubscribers.headers.participant'), align: 'center' },
        { value: 'administrator', title: this.$t('subscriptions.views.SubscriptionSubscribers.headers.administrator'), align: 'center' },
        { value: 'accounting', title: this.$t('subscriptions.views.SubscriptionSubscribers.headers.accounting'), align: 'center' },
        { value: 'archivingResponsible', title: this.$t('subscriptions.views.SubscriptionSubscribers.headers.archivingResponsible'), align: 'center' },
        { value: 'options', title: this.$t('subscriptions.views.SubscriptionSubscribers.headers.options') },
      ],
      expanded: [],
      deleteUserDialogIsOpen: false,
      subAddConfirmationDialogInfo: null,
      subRemoveConfirmationDialogInfo: null,
      userToDelete: null,
      searchQuery: '',
      subAddConfirmationDialogText: '',
      ITEMS_PER_PAGE,
    }
  },
  computed: {
    ...mapState('user', ['profile']),
    ...mapState('subscription', [
      'subscription',
      'subscriptionPending',
      'subscriptionUsers',
      'patchSubAdminPendingIds',
      'patchSubAdminBillingPendingIds',
      'patchSubAdminUserPendingIds',
      'seatsPending',
      'seats',
      'seatsError',
      'subscriptionUsersPending',
    ]),
    ...mapGetters('user', ['currentUserId']),
    filteredSubscriptionUsers () {
      if (this.searchQuery) {
        const query = this.searchQuery.toLowerCase()
        return this.subscriptionUsers.data.filter(user => {
          return user.firstName.toLowerCase().includes(query) ||
            user.lastName.toLowerCase().includes(query)
        })
      }
      return this.subscriptionUsers.data
    },
    exportLink () {
      return `${import.meta.env.VUE_APP_API_URL}/subscription/download/subscribers-excel`
    },
  },
  created () {
    this.prepareGetSubscriptionUsers()
  },
  methods: {
    ...mapActions('subscription', [GET_SUBSCRIPTION_USERS, PATCH_SUB_ADMIN, PATCH_SUB_ADMIN_BILLING, PATCH_SUB_ADMIN_USER, GET_SUBSCRIPTION, GET_SEATS]),
    onExpandClick (item) {
      if (this.expanded.includes(item.id)) {
        this.expanded = this.expanded.filter(id => id !== item.id)
      } else {
        this.expanded.push(item.id)
      }
    },
    async prepareGetSubscriptionUsers () {
      try {
        await this.GET_SUBSCRIPTION_USERS()
      } catch (error) {
        console.error(error)
        toast.error(this.$t('subscriptions.views.SubscriptionSubscribers.getSubscriptionUsersError'))
      }
    },
    async onSubAdminChange (item) {
      try {
        await this.PATCH_SUB_ADMIN({
          userId: item.id,
          data: {
            userIsAdmin: !item.isSubAdmin,
          },
        })
        toast.success(this.$t('subscriptions.views.SubscriptionSubscribers.patchSubAdminSuccess'))
      } catch (error) {
        if (error.response?.data?.errorCode === 'ERR_INCORRECT_DATA') {
          toast.error(this.$t('patchSubAdminERR_INCORRECT_DATA'))
        } else {
          toast.error(this.$t('subscriptions.views.SubscriptionSubscribers.patchSubAdminError'))
        }
      }
    },
    async onSubAdminBillingChange (item) {
      try {
        await this.PATCH_SUB_ADMIN_BILLING({
          userId: item.id,
          data: {
            userIsBillingAdmin: !item.isSubBillingAdmin,
          },
        })
        toast.success(this.$t('subscriptions.views.SubscriptionSubscribers.patchSubAdminSuccess'))
      } catch (error) {
        toast.error(this.$t('subscriptions.views.SubscriptionSubscribers.patchSubAdminError'))
      }
    },
    onSubAdminUserChange (item) {
      if (!item.isSubUser) {
        this.GET_SEATS()
          .then(() => { this.initSeatsData(this.seats) })
          .catch((error) => {
            console.error(error)
            this.subAddConfirmationDialogText = this.$t('subscriptions.views.SubscriptionSubscribers.getSeatsError')
          })
        this.subAddConfirmationDialogInfo = item
      } else {
        this.subRemoveConfirmationDialogInfo = item
      }
    },
    confirmSubAddConfirmationDialog () {
      const infos = this.subAddConfirmationDialogInfo
      this.subAddConfirmationDialogInfo = null
      this.prepareSubAdminUserChange(infos)
    },
    confirmSubRemoveConfirmationDialog () {
      const infos = this.subRemoveConfirmationDialogInfo
      this.subRemoveConfirmationDialogInfo = null
      this.prepareSubAdminUserChange(infos)
    },
    async prepareSubAdminUserChange (item) {
      try {
        // We invert the state here because it's the current state and not the new one
        // This is because of the dialog in-between state changes
        await this.PATCH_SUB_ADMIN_USER({
          userId: item.id,
          data: {
            userIsSubUser: !item.isSubUser,
            confirmMonthly: item.confirmMonthly,
            confirmYearly: item.confirmYearly,
          },
        })
        this.GET_SUBSCRIPTION(this.profile.subscription.id)
        toast.success(this.$t('subscriptions.views.SubscriptionSubscribers.patchSubAdminSuccess'))
      } catch (error) {
        toast.error(this.$t('subscriptions.views.SubscriptionSubscribers.patchSubAdminError'))
      }
    },
    initSeatsData (seats) {
      if (seats.billingSeatsAvailable <= 0 && (seats.nextYearlyPrice > 0 || seats.nextMonthlyPrice > 0)) {
        let price
        if (seats.nextYearlyPrice) {
          price = seats.nextYearlyPrice
          this.subAddConfirmationDialogInfo.confirmYearly = true
          this.subAddConfirmationDialogInfo.confirmMonthly = false
        } else {
          price = seats.nextMonthlyPrice
          this.subAddConfirmationDialogInfo.confirmMonthly = true
          this.subAddConfirmationDialogInfo.confirmYearly = false
        }
        this.subAddConfirmationDialogText = this.$t('subscriptions.views.SubscriptionSubscribers.licensePrice', { price })
      } else {
        this.subAddConfirmationDialogText = this.$t('subscriptions.views.SubscriptionSubscribers.licenseIncluded')
      }
    },
    cancelSubAddConfirmationDialog () {
      this.subAddConfirmationDialogInfo = null
    },
    cancelSubRemoveConfirmationDialog () {
      this.subRemoveConfirmationDialogInfo = null
    },
    closeDeleteUserDialog () {
      this.userToDelete = null
      this.deleteUserDialogIsOpen = false
    },
    openDeleteUserDialog (user) {
      this.userToDelete = user
      this.deleteUserDialogIsOpen = true
    },
    onPostNewSubscriberSuccess () {
      this.GET_SUBSCRIPTION(this.profile.subscription.id)
      this.prepareGetSubscriptionUsers()
    },
    userDeleteSuccess () {
      this.prepareGetSubscriptionUsers()
      this.closeDeleteUserDialog()
    },
  },
}
</script>

<style scoped lang="scss">
.SubscriptionSubscribers-card {
  border-radius: 8px;
}
:deep(.SubscriptionSubscribers-table) {
  background-color: transparent;
  table {
    border-collapse: separate;
    border-spacing: 0 8px;
  }
  th {
    color: rgb(var(--v-theme-text-primary)) !important;
    font-size: 12px !important;
    font-weight: 600 !important;
    text-transform: uppercase !important;
    border-bottom: none !important;
  }
  td {
    color: rgb(var(--v-theme-text-secondary)) !important;
    font-size: 14px !important;
    border-bottom: none !important;
    height: 48px !important;
    &:first-child {
      border-top-left-radius: 4px;
      border-bottom-left-radius: 4px;
    }
    &:last-child {
      border-top-right-radius: 4px;
      border-bottom-right-radius: 4px;
    }
  }
  tbody {
    tr {
      background-color: rgb(var(--v-theme-surface-primary));
      height: 60px;
    }
    tr.v-data-table__expanded__content {
      box-shadow: none;
    }
  }
}
.SubscriptionSubscribers-expand {
  transition-duration: 400ms;
  transition-property: transform;
}
.SubscriptionSubscribers-expand--open {
  transform: rotate(90deg);
}
.SubscriptionSubscribers-table-firstName {
  max-width: 100px;
}
.SubscriptionSubscribers-table-lastName {
  max-width: 110px;
}
.SubscriptionSubscribers-table-email {
  max-width: 200px;
}

.collapsed-info-title {
  color: rgb(var(--v-theme-text-primary));
  font-weight: bold;
  font-size: 12px;
  line-height: normal;
}

// Mobile
.v-data-table__td-value {
  .td-mobile-view {
    display: flex !important;
    justify-content: flex-end !important;
  }
  .SubscriptionSubscribers-table-firstName, .SubscriptionSubscribers-table-lastName, .SubscriptionSubscribers-table-email {
    max-width: 400px
  }
}
</style>
